/**
 * usePremiumPlan — FE-FP1-04
 *
 * Thin wrapper around useSubscription that returns only the current plan.
 * Used by PremiumPage to highlight the user's active plan in pricing cards.
 */
import { useSubscription } from '../subscription/useSubscription'
import { isExpiredSubscription } from '../subscription/subscriptionUtils'
import { useSubscriptionPlans } from '../subscription/useSubscriptionPlans'
import type { Plan } from '../subscription/useSubscriptionPlans'
import { planDisplayName } from './planDisplayName'
import { findCatalogPlan } from './legacyPlanMap'

export type PremiumPlanResult = {
  /** Nur bei aktivem Abo gesetzt — steuert die "Dein Plan"-Markierung. */
  plan: Plan | null
  /** True, sobald das Backend status === 'expired' liefert (Abo-Objekt bleibt bestehen). */
  isExpired: boolean
  /** Bei status === 'expired' der abgelaufene Plan (sonst null). */
  expiredPlan: Plan | null
  /** Anzeigename des abgelaufenen Plans — Fallback auf plan_id, falls unbekannt. */
  expiredPlanName: string | null
  /**
   * Anzeigename des aktuellen (oder abgelaufenen) Plans. Faellt der Plan aus
   * dem Katalog, wird die rohe plan_id lesbar formatiert statt roh gezeigt.
   */
  planName: string | null
  /** Laufzeitende des (abgelaufenen) Abos, ISO-String oder null. */
  expiresAt: string | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function usePremiumPlan(): PremiumPlanResult {
  const { subscription, isLoading, isError, refetch } = useSubscription()
  const { plans } = useSubscriptionPlans()

  const isExpired = isExpiredSubscription(subscription)
  // P1-1: Legacy-Plan-IDs (z.B. "premium" aus v1) matchen keinen SPEC-Tarif
  // direkt — zentrales Mapping statt rohem `.find()`.
  const matchedPlan: Plan | null = subscription
    ? findCatalogPlan(plans, subscription.plan_id)
    : null

  const planName =
    matchedPlan?.name ?? planDisplayName(subscription?.plan_id) ?? null

  return {
    planName,
    plan: isExpired ? null : matchedPlan,
    isExpired,
    expiredPlan: isExpired ? matchedPlan : null,
    expiredPlanName: isExpired ? planName : null,
    expiresAt: subscription?.current_period_end ?? null,
    isLoading: isLoading || !plans,
    isError,
    refetch,
  }
}
